import { Field, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Table } from "reactstrap";
import Swal from "sweetalert2";
import { purchaseActions } from "../../sagas/purchaseSlice";
import { supplierActions } from "../../sagas/supplierSlice";
import { FaBuilding, FaBusinessTime, FaEdit, FaEllipsisH, FaEnvelope, FaFilePdf, FaIdCard, FaList, FaMapMarkerAlt, FaMoneyBillWave, FaPhone, FaPlus, FaTruck, FaUser } from "react-icons/fa";
import Select from "react-select";
import { components } from 'react-select';
import { Spinner } from 'reactstrap';
import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import { formatPrice } from "../../helpers/utility";
import CommandeLine from "./CommandeLine";
import { transporteurActions } from "../../sagas/transporteurSlice";
import ReceiptNoteNew from "../Reception/ReceiptNoteNew";

const initForm = {
    numero: '',
    fournisseurId: null,
    transporteurId: null,
    numeroCheque: '',
    fournisseurNom: '',
    dateCommande: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    dateOperation: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    createdAt: null,
    updatedAt: null,
    lignes: [],
    statut: 'EN_ATTENTE',
    montantTotalHT: 0,
    montantTotalTaxe: 0,
    montantTotalTTC: 0,
    modeReglement: 'ESPECES',
    addresseLivraison: ''
};

const roundAmount = (value) => Math.round(Number(value) || 0);
const formatRoundedAmount = (value) =>
    `${new Intl.NumberFormat('fr-MA', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(roundAmount(value))} DH`;

const PurchaseForm = ({ onClose, purchase }) => {
    const { t } = useTranslation();
    const [formState, setForm] = useState(purchase || initForm);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { company } = useSelector(statut => statut.auth);
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const { transporteurs } = useSelector(state => state.transporteurs);
    const [fournisseur, setFournisseur] = useState({
        nom: '',
        prenom: '',
        cin: '',
        telephone1: '',
        telephone2: '',
        email: '',
        adresse: '',
        ville: 'Casablanca',
        typeFournisseur: 'PARTICULIER',
        codePostal: '',
        plafond: 0,
        entreprise: '', // New field for "Entreprise"
        ice: '',// New field for "ICE",
        active: true,
        solde: 0 // Ce champ sera en lecture seule
    });

    const { newOrderNumber, isLoading } = useSelector(statut => statut.purchases);
    const [showReceiptNoteModal, setShowReceiptNoteModal] = useState(false);
    const [receiptNoteForm, setReceiptNoteForm] = useState(null);
    const { receiptNotes, newBRNumber, isLoading: receiptNoteLoading } = useSelector(statut => statut.receiptNote);
    const [receiptNoteCount, setReceiptNoteCount] = useState(0);
    const [showReceiptNoteListModal, setShowReceiptNoteListModal] = useState(false);
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
    const [errors, setErrors] = useState({});
    const [allReceived, setAllReceived] = useState(false);

    const [filters, setFilters] = useState({
        fournisseurId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    const villesMaroc = [
        'Casablanca', 'Rabat', 'Khouribga', 'Marrakech', 'Fès', 'Tanger',
        'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan',
        'Safi', 'Mohammedia', 'El Jadida', 'Nador', 'Beni Mellal'
    ];



    useEffect(() => {
        dispatch(supplierActions.search({
            query: ""
        }));

        dispatch(purchaseActions.fetchNewOrderNumber());
        dispatch(transporteurActions.list());

    }, [])

    useEffect(() => {

    }, [suppliers])

    const transporteurOptions = transporteurs?.map(transporteur => ({
        value: transporteur.id,
        label: transporteur.code + ' - ' + transporteur.libelle,
    })) ?? [];

    const transporteurOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...transporteurOptions
    ];

    const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];

    useEffect(() => {
        if (purchase) {
            fetchReceiptNotesByPurchaseId(purchase.id);
        }
    }, [purchase, dispatch]);

    useEffect(() => {
        if (purchase && receiptNotes) {
            setReceiptNoteCount(receiptNotes.length);
        }
    }, [receiptNotes]);

    const formik = useFormik({
        initialValues: { ...formState, numero: purchase ? formState.numero : newOrderNumber, addresseLivraison: purchase ? formState.addresseLivraison : company.address },
        enableReinitialize: true,
        onSubmit: (values) => {
            if (!values.transporteurId) {
                Swal.fire({
                    icon: 'warning',
                    title: t('sale.errorTitle'),
                    text: t('sale.transportRequired')
                });
                return;
            }

            Swal.fire({
                title: t('purchase.confirmation'),
                text: t('purchase.saveOrConfirmText'),
                icon: 'question',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: t('common.saveAndConfirm'),
                denyButtonText: t('common.save'),
                cancelButtonText: t('common.cancel'),

            }).then((result) => {
                if (result.isConfirmed) {
                    values.statut = 'VALIDE';
                    values.updatedAt = null;
                    values.createdAt = null;
                    values.montantTotalTTC = roundAmount(values.montantTotalTTC);

                    const payload = {
                        data: values,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('common.saved'),
                                text: t('purchase.savedSuccess'),
                            });
                            onClose();
                        }
                    };

                    if (purchase) {
                        dispatch(purchaseActions.updatePurchase({ ...payload, id: purchase.id }));
                    } else {
                        dispatch(purchaseActions.createPurchase(payload));
                    }
                } else if (result.isDenied) {
                    values.statut = 'EN_ATTENTE';
                    values.updatedAt = null;
                    values.createdAt = null;
                    values.montantTotalTTC = roundAmount(values.montantTotalTTC);
                    const payload = {
                        data: values,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('common.saved'),
                                text: t('purchase.savedSuccess'),
                            });
                            onClose();
                        }
                    };

                    if (purchase) {
                        dispatch(purchaseActions.updatePurchase({ ...payload, id: purchase.id }));
                    } else {
                        dispatch(purchaseActions.createPurchase(payload));
                    }
                } else {

                }
            });
        }
    });

    const { values, touched, getFieldProps, handleSubmit, setFieldValue } = formik;

    // Determine if all ordered quantities have been received
    useEffect(() => {
        if (!values || !values.lignes || values.lignes.length === 0) {
            setAllReceived(false);
            return;
        }

        // Sum received quantities per article from receiptNotes
        const receivedMap = {};
        (receiptNotes || []).forEach(note => {
            (note.lignes || []).forEach(line => {
                const key = line.articleId || line.articleCode || line.articleNom;
                receivedMap[key] = (receivedMap[key] || 0) + (Number(line.quantiteRecue) || 0);
            });
        });

        // Check each ordered line
        const allOk = values.lignes.every(line => {
            const key = line.articleId || line.articleCode || line.articleNom;
            const ordered = Number(line.quantiteCommandee || line.quantite || 0);
            const received = Number(receivedMap[key] || 0);
            return received >= ordered;
        });

        setAllReceived(allOk);
    }, [values.lignes, receiptNotes]);


    const fetchReceiptNotesByPurchaseId = (id) => {
        dispatch(receiptNoteActions.getReceiptNotesByPurchaseId({ id }));
    }

    const handleShowReceiptNoteList = () => {
        setShowReceiptNoteListModal(true);
    };

    const handleCloseReceiptNoteListModal = () => {
        setShowReceiptNoteListModal(false);
    };



    // Add this function to handle opening the receipt note modal
    const handleCreateReception = () => {
        // Populate receipt note form with data from purchase order
        const newReceiptNote = {
            commandeId: values.id,
            fournisseurId: values.fournisseurId,
            fournisseurNom: values.fournisseurNom,
            transporteurId: values.transporteurId,
            dateReception: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            dateOperation: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            modeReglement: values.modeReglement,
            lignes: values.lignes.map(line => ({
                articleId: line.articleId,
                articleNom: line.articleNom,
                quantiteCommandee: line.quantiteCommandee,
                quantiteRecue: line.quantiteCommandee,
                prixUnitaireHT: line.prixUnitaireHT,
                prixUnitaireTTC: line.prixUnitaireTTC,
                montantHT: line.montantHT,
                tauxTVA: line.tauxTVA,
                montantTTC: line.montantTTC
            })),
            montantTotalHT: values.montantTotalHT,
            montantTotalTTC: values.montantTotalTTC,
            montantTotalTaxe: values.montantTotalTaxe,
            statut: 'EN_ATTENTE',
        };

        dispatch(receiptNoteActions.fetchNewBRNumber());
        setReceiptNoteForm(newReceiptNote);
        setShowReceiptNoteModal(true);
    };

    // Add this function to handle closing the receipt note modal
    const handleCloseReceiptNoteModal = () => {
        setShowReceiptNoteModal(false);
        setReceiptNoteForm(null);
        fetchReceiptNotesByPurchaseId(values.id);
    };


    const handleSupplierChange = (selectedOption) => {
        formik.setFieldValue('lignes', []); // Clear the lignes when the supplier changes
        formik.setFieldValue('fournisseurId', selectedOption?.value || null);
        formik.setFieldValue('fournisseurNom', selectedOption?.label || '');
        formik.setFieldValue('montantTotalTTC', 0);
        formik.setFieldValue('montantTotalHT', 0);
        formik.setFieldValue('montantTotalTaxe', 0);
    };





    const handleCreateSupplier = () => {
        setShowSupplierModal(true);
    };



    const handleSupplierSubmit = () => {
        const newErrors = {};
        // Common validations
        if (!fournisseur.telephone1) newErrors.telephone1 = 'Téléphone obligatoire';
        if (!fournisseur.adresse) newErrors.adresse = 'Adresse obligatoire';

        // Validations for "PARTICULIER"
        if (fournisseur.typeFournisseur === 'PARTICULIER') {
            if (!fournisseur.nom) newErrors.nom = 'Nom obligatoire';
            if (!fournisseur.prenom) newErrors.prenom = 'Prénom obligatoire';
        }

        // Validations for "ENTREPRISE"
        if (fournisseur.typeFournisseur === 'ENTREPRISE') {
            if (!fournisseur.entreprise) newErrors.entreprise = 'Nom de l\'entreprise obligatoire';
            if (!fournisseur.ice) newErrors.ice = 'Numéro ICE obligatoire';
        }


        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let payload = {
            data: {
                ...fournisseur,
            },

            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: 'Succès',
                    text: 'Client enregistrés avec succès!',
                    confirmButtonText: 'OK',
                });

                setFournisseur({
                    nom: '',
                    prenom: '',
                    cin: '',
                    telephone1: '',
                    telephone2: '',
                    typeFournisseur: 'PARTICULIER',
                    email: '',
                    adresse: '',
                    ville: 'Casablanca',
                    codePostal: '',
                    plafond: 0,
                    entreprise: '',
                    ice: '',
                    solde: 0,
                    active: true,
                });

                setShowSupplierModal(false);
                setErrors({});
            },
        };


        dispatch(supplierActions.addSupplier(payload));

    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'EN_ATTENTE':
                return <Badge color="secondary">En attente confirmation</Badge>;
            case 'VALIDE':
                return <Badge color="info">Commandé</Badge>;
            case 'LIVRE':
                return <Badge color="primary">Livré</Badge>;
            case 'ANNULE':
                return <Badge color="danger">Annulé</Badge>;
            default:
                return <Badge color="light">-</Badge>;
        }
    };

    const getReceptionStatusBadge = (status) => {
        switch (status) {
            case 'EN_ATTENTE':
                return <Badge color="secondary">En attente confirmation</Badge>;
            case 'VALIDE':
                return <Badge color="success">Confirmée</Badge>;

            default:
                return <Badge color="light">-</Badge>;
        }
    };


    const handleFournisseurChange = (e) => {
        const { name, value } = e.target;
        setFournisseur((prev) => ({
            ...prev,
            [name]: name === 'active' ? value === 'true' : value, // Convert "true"/"false" to boolean for the active field
        }));
    };

    return (
        <Container fluid>
            {(isLoading || receiptNoteLoading) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                </div>
            )}
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row className="align-items-center">
                            <Col md="10">
                                <Row>
                                    <Col md="2">
                                        <Label htmlFor="numero">{t('purchase.orderNumber')}:</Label>
                                        <Input
                                            type="text"
                                            id="numero"
                                            {...getFieldProps('numero')}
                                            disabled
                                            className="form-control"
                                        />
                                    </Col>

                                    <Col md="3">
                                        <Label htmlFor="supplier">{t('purchase.supplier')}:</Label>
                                        <Select
                                            id="supplier"
                                            name="supplier"
                                            isClearable
                                            placeholder={t('purchase.selectSupplier')}
                                            options={fournisseurOptions}
                                            onChange={handleSupplierChange}
                                            value={fournisseurOptions.find(option => option.value === values.fournisseurId)}
                                            className="react-select"
                                            classNamePrefix="select"
                                            noOptionsMessage={() => t('purchase.noSupplierFound')}
                                            loadingMessage={() => t('common.loading')}
                                            components={{
                                                DropdownIndicator: () => <FaUser className="me-2" />,
                                                Option: ({ children, ...props }) => (
                                                    <components.Option {...props}>{children}</components.Option>
                                                ),
                                                MenuList: ({ children, ...props }) => (
                                                    <components.MenuList {...props}>
                                                        {children}
                                                        <div className="border-top p-2">
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                block
                                                                onClick={handleCreateSupplier}
                                                            >
                                                                <FaPlus className="me-1" /> {t('supplier.new')}
                                                            </Button>
                                                        </div>
                                                    </components.MenuList>
                                                )
                                            }}
                                        />
                                        {formik.touched.fournisseurId && formik.errors.fournisseurId && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.fournisseurId}
                                            </div>
                                        )}
                                    </Col>
                                    <Col md="3">
                                        <Label htmlFor="transporteurId">{t('bonLivraison.transporteur')}:</Label>
                                        <Select
                                            id="transporteurId"
                                            isClearable
                                            options={transporteurOptionsWithAll}
                                            value={transporteurOptionsWithAll.find(option => option.value === values.transporteurId)}
                                            onChange={(selectedOption) => setFieldValue('transporteurId', selectedOption ? selectedOption.value : null)}
                                            placeholder={t('bonLivraison.selectTransporteur')}
                                            isSearchable
                                            isDisabled={false}
                                        />
                                        {errors.transporteurId && touched.transporteurId && (
                                            <div className="text-danger small mt-1">{errors.transporteurId}</div>
                                        )}
                                    </Col>
                                    <Col md="3">
                                        <Label htmlFor="orderDate">{t('purchase.orderDate')}:</Label>
                                        <Input
                                            type="date"
                                            id="orderDate"
                                            {...getFieldProps('dateCommande')}
                                            className="form-control"
                                            disabled={false}
                                        />
                                    </Col>
                                    <Col md="3">
                                        <Label htmlFor="dateOperation">{t('commande.dateOperation')}:</Label>
                                        <Input
                                            type="date"
                                            id="dateOperation"
                                            {...getFieldProps('dateOperation')}
                                            className="form-control"
                                            disabled={false}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="modeReglement">
                                                <FaMoneyBillWave className="me-1" />
                                                {t('facture.modeReglement')} *
                                            </Label>
                                            <Input
                                                type="select"
                                                id="modeReglement"
                                                name="modeReglement"
                                                value={values.modeReglement}
                                                onChange={(e) => formik.setFieldValue('modeReglement', e.target.value)}
                                            >
                                                <option value="">{t('common.select')}</option>
                                                <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                                <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                                <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                                <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                                <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    {(values.modeReglement === 'CHEQUE') && (

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label for="numeroCheque">{t('reglement.numeroCheque')}</Label>
                                                <Input
                                                    name="numeroCheque"
                                                    type="text"
                                                    className="form-control"
                                                    value={values.numeroCheque}
                                                    onChange={(e) => formik.setFieldValue('numeroCheque', e.target.value)}
                                                    disabled={false}
                                                />
                                            </FormGroup>
                                        </Col>)}
                                </Row>
                            </Col>

                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <CommandeLine formik={formik} disabled={false} />
                        </Row>
                        <Row className="mt-3">
                            <Col md="8">
                                <Label htmlFor="addresseLivraison">{t('purchase.deliveryAddress')}:</Label>
                                <Input
                                    type="textarea"
                                    id="addresseLivraison"
                                    {...getFieldProps('addresseLivraison')}
                                    className="form-control"
                                    defaultValue={company.address} // Set default value to company address
                                />
                            </Col>
                        </Row>
                        <Row className="mt-3 justify-content-end">
                            <Col md={4}>
                                <div className="totals text-center">
                                    <p><strong>{t('cart.totalHT')}:</strong> {formatPrice(values.montantTotalHT ?? 0)}</p>
                                    <p><strong>{t('cart.totalTVA')}:</strong> {formatPrice(values.montantTotalTaxe ?? 0)}</p>
                                    <p><strong>{t('cart.totalTTC')}:</strong> {formatRoundedAmount(values.montantTotalTTC ?? 0)}</p>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" color="primary" className="me-2"
                                    disabled={false}
                                >
                                    {t('common.save')}
                                </Button>
                                <Button type="button" color="secondary" onClick={onClose}>
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>
            <Modal isOpen={showSupplierModal} toggle={() => setShowSupplierModal(false)} size="xl">
                <ModalHeader toggle={() => setShowSupplierModal(false)}>
                    {t('supplier.new')}
                </ModalHeader>
                <ModalBody>
                    <style>
                        {`
                                    .form-floating {
                                        margin-bottom: 1rem;
                                    }
                                    .form-floating > .form-control-sm {
                                        height: calc(2.5rem + 2px);
                                        padding: 0.5rem 0.75rem;
                                        font-size: 0.875rem;
                                        border-radius: 0.375rem;
                                    }
                                    .form-floating > label {
                                        padding: 0.5rem 0.75rem;
                                        font-size: 0.875rem;
                                    }
                                    .form-control-sm {
                                        transition: all 0.2s ease-in-out;
                                    }
                                    .form-control-sm:focus {
                                        background-color: #fff !important;
                                        box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
                                        border-color: #86b7fe !important;
                                    }
                                    .fournisseur-type-buttons {
                                        display: inline-flex;
                                        background: #f8f9fa;
                                        padding: 0.25rem;
                                        border-radius: 0.5rem;
                                        gap: 0.25rem;
                                        margin-bottom: 1.5rem;
                                    }
                                    .fournisseur-type-btn {
                                        border: none;
                                        background: transparent;
                                        padding: 0.5rem 1rem;
                                        border-radius: 0.375rem;
                                        display: flex;
                                        align-items: center;
                                        gap: 0.5rem;
                                        font-size: 0.875rem;
                                        color: #6c757d;
                                        transition: all 0.2s;
                                    }
                                    .fournisseur-type-btn.active {
                                        background: white;
                                        color: #0d6efd;
                                        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                                    }
                                    .fournisseur-type-btn:hover:not(.active) {
                                        background: rgba(255,255,255,0.5);
                                    }
                                    .required-field::after {
                                        content: ' *';
                                        color: #dc3545;
                                    }
                                    .g-3 {
                                        --bs-gutter-y: 1rem;
                                    }
                                `}
                    </style>
                    <div className="fournisseur-type-toggle">
                        <div className="fournisseur-type-buttons">
                            <button
                                type="button"
                                className={`fournisseur-type-btn ${fournisseur.typeFournisseur === 'PARTICULIER' ? 'active' : ''}`}
                                onClick={() => handleFournisseurChange({ target: { name: 'typeFournisseur', value: 'PARTICULIER' } })}
                            >
                                <FaUser />
                                Particulier
                            </button>
                            <button
                                type="button"
                                className={`fournisseur-type-btn ${fournisseur.typeFournisseur === 'ENTREPRISE' ? 'active' : ''}`}
                                onClick={() => handleFournisseurChange({ target: { name: 'typeFournisseur', value: 'ENTREPRISE' } })}
                            >
                                <FaBuilding />
                                Entreprise
                            </button>
                        </div>
                    </div>

                    {/* Particulier Fields */}
                    {fournisseur.typeFournisseur === 'PARTICULIER' && (
                        <Row className="fournisseur-form-row">
                            <Col md={4}>
                                <FormGroup className="fournisseur-form-group">
                                    <Label for="nom" className="fournisseur-form-label required-field">
                                        <FaUser />
                                        Nom
                                    </Label>
                                    <Input
                                        type="text"
                                        id="nom"
                                        name="nom"
                                        value={fournisseur.nom}
                                        onChange={handleFournisseurChange}
                                        className={`fournisseur-form-input ${errors.nom ? 'is-invalid' : ''}`}
                                        placeholder="El Fassi"
                                    />
                                    {errors.nom && <div className="fournisseur-form-error">{errors.nom}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup className="fournisseur-form-group">
                                    <Label for="prenom" className="fournisseur-form-label required-field">
                                        <FaUser />
                                        Prénom
                                    </Label>
                                    <Input
                                        type="text"
                                        id="prenom"
                                        name="prenom"
                                        value={fournisseur.prenom}
                                        onChange={handleFournisseurChange}
                                        className={`fournisseur-form-input ${errors.prenom ? 'is-invalid' : ''}`}
                                        placeholder="Karim"
                                    />
                                    {errors.prenom && <div className="fournisseur-form-error">{errors.prenom}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup className="fournisseur-form-group">
                                    <Label for="cin" className="fournisseur-form-label">
                                        <FaIdCard />
                                        CIN
                                    </Label>
                                    <Input
                                        type="text"
                                        id="cin"
                                        name="cin"
                                        value={fournisseur.cin}
                                        onChange={handleFournisseurChange}
                                        className="fournisseur-form-input"
                                        placeholder="AB123456"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    {/* Entreprise Fields */}
                    {fournisseur.typeFournisseur === 'ENTREPRISE' && (
                        <Row className="fournisseur-form-row">
                            <Col md={6}>
                                <FormGroup className="fournisseur-form-group">
                                    <Label for="entreprise" className="fournisseur-form-label required-field">
                                        <FaBuilding />
                                        Nom de l'entreprise
                                    </Label>
                                    <Input
                                        type="text"
                                        id="entreprise"
                                        name="entreprise"
                                        value={fournisseur.entreprise}
                                        onChange={handleFournisseurChange}
                                        className={`fournisseur-form-input ${errors.entreprise ? 'is-invalid' : ''}`}
                                        placeholder="Nom de l'entreprise"
                                    />
                                    {errors.entreprise && <div className="fournisseur-form-error">{errors.entreprise}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup className="fournisseur-form-group">
                                    <Label for="ice" className="fournisseur-form-label required-field">
                                        <FaIdCard />
                                        Numéro ICE
                                    </Label>
                                    <Input
                                        type="text"
                                        id="ice"
                                        name="ice"
                                        value={fournisseur.ice}
                                        onChange={handleFournisseurChange}
                                        className={`fournisseur-form-input ${errors.ice ? 'is-invalid' : ''}`}
                                        placeholder="000123456789012"
                                    />
                                    {errors.ice && <div className="fournisseur-form-error">{errors.ice}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <Row className="g-3">
                        <Col md={3}>
                            <FormGroup>
                                <Label for="telephone1">
                                    <FaPhone className="me-2" size={12} />
                                    Téléphone (1) *
                                </Label>
                                <Input
                                    type="tel"
                                    id="telephone1"
                                    name="telephone1"
                                    value={fournisseur.telephone1}
                                    onChange={handleFournisseurChange}
                                    invalid={!!errors.telephone1}
                                    placeholder="06 12 34 56 78"
                                    className="py-2"
                                />

                                {errors.telephone1 && <div className="text-danger small mt-1">{errors.telephone1}</div>}
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="telephone2">
                                    <FaPhone className="me-2" size={12} />
                                    Téléphone (2) *
                                </Label>
                                <Input
                                    type="tel"
                                    id="telephone2"
                                    name="telephone2"
                                    value={fournisseur.telephone2}
                                    onChange={handleFournisseurChange}
                                    invalid={!!errors.telephone2}
                                    placeholder="06 12 34 56 78"
                                    className="py-2"
                                />

                                {errors.telephone2 && <div className="text-danger small mt-1">{errors.telephone2}</div>}
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="email" lassName="fw-bold">
                                    <FaEnvelope className="me-2" size={12} />
                                    Email
                                </Label>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={fournisseur.email}
                                    onChange={handleFournisseurChange}
                                    placeholder="k.elfassi@example.com"
                                    className="py-2"
                                />

                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup className="mb-4">
                                <Label for="ville" className="fw-bold">
                                    <FaMapMarkerAlt className="me-2 text-muted" />
                                    Ville
                                </Label>
                                <Input
                                    type="select"
                                    id="ville"
                                    name="ville"
                                    value={fournisseur.ville}
                                    onChange={handleFournisseurChange}
                                    className="py-2"
                                >
                                    {villesMaroc.map(ville => (
                                        <option key={ville} value={ville}>{ville}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>

                    </Row>
                    <Row>
                        <Col md={8}>
                            <FormGroup className="mb-4">
                                <Label for="adresse" className="fw-bold">
                                    <FaMapMarkerAlt className="me-2 text-muted" />
                                    Adresse*
                                </Label>
                                <Input
                                    type="textarea"
                                    rows="2"
                                    id="adresse"
                                    name="adresse"
                                    value={fournisseur.adresse}
                                    onChange={handleFournisseurChange}
                                    placeholder="12 Rue Moulay Rachid"
                                    className="py-2"
                                />
                                {errors.adresse && <div className="text-danger small mt-1">{errors.adresse}</div>}
                            </FormGroup>
                        </Col>


                        <Col md={3}>
                            <FormGroup className="mb-4">
                                <Label for="active" className="fw-bold">
                                    <FaBusinessTime className="me-2 text-muted" />
                                    Statut Actif*
                                </Label>
                                <Input
                                    type="select"
                                    id="active"
                                    name="active"
                                    value={fournisseur.active.toString()} // Convert boolean to string for the select element
                                    onChange={handleFournisseurChange}
                                    className="py-2"
                                >
                                    <option value="true">Actif</option>
                                    <option value="false">Inactif</option>
                                </Input>
                            </FormGroup>
                        </Col>

                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleSupplierSubmit}>
                        {t('common.save')}
                    </Button>
                    <Button color="secondary" onClick={() => setShowSupplierModal(false)}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={showReceiptNoteModal} toggle={handleCloseReceiptNoteModal} size="xl">
                <ModalHeader toggle={handleCloseReceiptNoteModal}>
                    {t('reception.new')} - {receiptNoteForm?.numero || newBRNumber}
                </ModalHeader>
                <ModalBody>
                    {receiptNoteForm && (
                        <ReceiptNoteNew
                            initialValues={receiptNoteForm}
                            onClose={handleCloseReceiptNoteModal}
                            purchaseOrder={values}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showReceiptNoteListModal} toggle={handleCloseReceiptNoteListModal} size="xl">
                <ModalHeader toggle={handleCloseReceiptNoteListModal}>
                    {t('reception.receiptNotes')}
                </ModalHeader>
                <ModalBody>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>{t('reception.receiptNumber')}</th>
                                <th>{t('reception.dateReception')}</th>
                                <th>{t('reception.deliveryNoteNb')}</th>
                                <th>{t('reception.statut')}</th>
                                <th className="align-middle text-center">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {receiptNotes && receiptNotes.map(note => (
                                <tr key={note.id}>
                                    <td>{note.numero}</td>
                                    <td>{new Date(note.dateReception).toLocaleDateString()}</td>
                                    <td>{note.deliveryNoteNb}</td>
                                    <td>{getReceptionStatusBadge(note.statut)}</td>
                                    <td className="align-middle text-center">
                                        <>
                                            {/* <Button color="secondary" title='Téléchargé PDF' onClick={() => handleGeneratePdf(note)} size="sm" className="me-1 px-2 py-1">
                                                <FaFilePdf size={12} className="mr-2" />
                                            </Button> */}

                                            <Button color="warning" title={allReceived ? t('reception.allReceived') : 'Consulter'} onClick={() => {
                                                setReceiptNoteForm(note);
                                                setShowReceiptNoteModal(true);
                                            }} size="sm" className="px-2 py-1" disabled={allReceived}>
                                                <FaEdit size={12} className="mr-2" />
                                            </Button>
                                        </>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCloseReceiptNoteListModal}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default PurchaseForm;
